import React from 'react'
import { useParams } from 'react-router'

import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { AddPersonDrawerOpenable } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/AddPersonDrawer'
import { useGetPeopleInfoDataLazyQuery } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/GetPeopleInfoData.gql'
import LimitedAddPersonDrawer from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/LimitedAddPersonDrawer'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { LimitedAddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

export interface UpdateOutsourcedAccountantDrawerProps extends AddPersonDrawerOpenable {
  personId: string
}

const UpdateOutsourcedAccountantDrawer = ({ isShowing, onClose, personId }: UpdateOutsourcedAccountantDrawerProps) => {
  const { appId } = useParams()
  const showToast = useToast()
  const { track } = useWebTracking()

  const [getOutsourcedAccountant] = useGetPeopleInfoDataLazyQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const [updateOutsourcedAccountant] = useUpdatePeopleInfoData({
    onCompleted: () => {
      logInfo('updateOutsourcedAccountant', {
        attributes: {
          creditAppId: appId,
          result: `outsourced accountant updated successfully`,
        },
        eventName: 'updateOutsourcedAccountant',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Outsourced accountant updated'
          id='sputnik.UpdateOutsourcedAccountantDrawer__s7Q6Yl'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      onClose()
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Unable to update outsourced accountant'
          id='sputnik.UpdateOutsourcedAccountantDrawer__PpAxVw'
        />,
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'updateOutsourcedAccountant',
          creditAppId: appId,
          result: error,
          message: 'Error while updating outsourced accountant.',
        },
        eventName: 'updateOutsourcedAccountant',
      })
    },
  })

  const initialValues = async (): Promise<LimitedAddPersonForm> => {
    const { data } = await getOutsourcedAccountant()

    const { email, firstName, lastName } = data?.creditApplication?.outsourcedAccountant ?? {}

    return {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
    }
  }

  const onSubmit = async (formValues: LimitedAddPersonForm) => {
    track('se_signup__edit_person_drawer_saved__fired', {
      person_type: 'outsourcedAccountant',
    })
    await updateOutsourcedAccountant({
      variables: {
        creditApplicationId: appId ?? '',
        hasOutsourcedAccountant: true,
        outsourcedAccountant: {
          id: personId,
          email: formValues.email || '',
          firstName: formValues.firstName || '',
          lastName: formValues.lastName || '',
        },
      },
    })
  }

  return (
    isShowing && (
      <LimitedAddPersonDrawer
        initialValues={initialValues}
        isShowing={isShowing}
        title={
          <FormattedMessage
            defaultMessage='Update outsourced accountant'
            id='sputnik.UpdateOutsourcedAccountantDrawer__QkiEBZ'
          />
        }
        onClose={() => {
          track('se_signup__edit_person_drawer_cancelled__fired', {
            person_type: 'outsourcedAccountant',
          })
          onClose()
        }}
        onSubmit={onSubmit}
      />
    )
  )
}

export default UpdateOutsourcedAccountantDrawer
